// Pure text-chunking helpers for the client-side BYOM pipeline.
// No React, no pdfjs — fully unit-testable under Node.

// Rough token estimate: ~4 characters per token for technical English text.
export const CHARS_PER_TOKEN = 4;

export const DEFAULT_TARGET_TOKENS = 900; // ~800–1000 requested
export const DEFAULT_OVERLAP_TOKENS = 150;

// A PDF whose pages average fewer characters than this is almost certainly a
// scan (images of pages, no text layer).
export const SCANNED_AVG_CHARS_PER_PAGE = 100;

export function approxTokens(text) {
  return Math.ceil(String(text || '').length / CHARS_PER_TOKEN);
}

// Control characters PostgreSQL can't store in text columns: everything
// below 0x20 except \n and \t, plus DEL. The null byte (u+0000) in
// particular makes Postgres fail with `22P05: unsupported Unicode escape
// sequence` — the root cause of the "Could not store the chunks" bug.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
const HAS_SURROGATE_RE = /[\uD800-\uDFFF]/;

// Drops unpaired UTF-16 surrogates (garbage from broken PDF encodings) while
// keeping valid surrogate pairs (emoji, CJK extensions) intact. Written as a
// scan instead of a lookbehind regex for older-Safari compatibility.
function stripUnpairedSurrogates(text) {
  if (!HAS_SURROGATE_RE.test(text)) return text;
  const out = [];
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        out.push(text[i], text[i + 1]);
        i++;
      }
      // else: unpaired high surrogate — drop
    } else if (code < 0xdc00 || code > 0xdfff) {
      out.push(text[i]);
    }
    // unpaired low surrogate — drop
  }
  return out.join('');
}

// Cleans extracted PDF text so it is safe for PostgreSQL text columns and
// embedding calls: removes null bytes and other control characters (keeping
// \n and \t), strips unpaired surrogates, and normalizes whitespace without
// destroying the blank-line paragraph structure that chunkText relies on.
export function sanitizeText(text) {
  return stripUnpairedSurrogates(String(text || ''))
    .replace(/\r\n?/g, '\n')
    .replace(CONTROL_CHARS_RE, '')
    .replace(/[ \t]+\n/g, '\n') // trailing spaces on lines
    .replace(/[ \t]{2,}/g, ' ') // runs of spaces/tabs
    .replace(/\n{3,}/g, '\n\n') // 3+ blank lines -> one paragraph break
    .trim();
}

// pageTexts: array of per-page extracted strings.
export function isProbablyScanned(pageTexts, minAvgCharsPerPage = SCANNED_AVG_CHARS_PER_PAGE) {
  if (!Array.isArray(pageTexts) || pageTexts.length === 0) return true;
  const totalChars = pageTexts.reduce((sum, p) => sum + String(p || '').trim().length, 0);
  return totalChars / pageTexts.length < minAvgCharsPerPage;
}

// Splits an oversized paragraph into pieces of at most maxChars, breaking on
// sentence ends where possible, otherwise on whitespace, otherwise hard.
function splitLongParagraph(paragraph, maxChars) {
  const pieces = [];
  let rest = paragraph;
  while (rest.length > maxChars) {
    const window = rest.slice(0, maxChars);
    // Prefer the last sentence end in the window, then the last whitespace.
    let cut = Math.max(
      window.lastIndexOf('. '),
      window.lastIndexOf('.\n'),
      window.lastIndexOf('! '),
      window.lastIndexOf('? ')
    );
    if (cut > maxChars * 0.3) {
      cut += 1; // keep the punctuation with the left piece
    } else {
      const ws = window.lastIndexOf(' ');
      cut = ws > maxChars * 0.3 ? ws : maxChars;
    }
    pieces.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) pieces.push(rest);
  return pieces;
}

// Takes the trailing `overlapChars` of a chunk, starting at a word boundary.
function tailForOverlap(chunk, overlapChars) {
  if (chunk.length <= overlapChars) return chunk;
  const tail = chunk.slice(-overlapChars);
  const firstSpace = tail.indexOf(' ');
  return firstSpace === -1 ? tail : tail.slice(firstSpace + 1);
}

// Splits `text` into overlapping chunks of ~targetTokens, preferring paragraph
// boundaries. Returns an array of non-empty strings.
export function chunkText(
  text,
  { targetTokens = DEFAULT_TARGET_TOKENS, overlapTokens = DEFAULT_OVERLAP_TOKENS } = {}
) {
  // sanitizeText also runs upstream (per page in pdfExtract), but chunkText
  // must be safe standalone — defense in depth against dirty input.
  const clean = sanitizeText(text);
  if (!clean) return [];

  const maxChars = targetTokens * CHARS_PER_TOKEN;
  const overlapChars = overlapTokens * CHARS_PER_TOKEN;

  // Paragraphs: blank-line separated. Oversized paragraphs are pre-split so
  // every unit below fits in a chunk.
  const units = clean
    .split(/\n\s*\n+/)
    .map(p => p.trim())
    .filter(Boolean)
    .flatMap(p => (p.length > maxChars ? splitLongParagraph(p, maxChars) : [p]));

  const chunks = [];
  let current = '';

  for (const unit of units) {
    const candidate = current ? `${current}\n\n${unit}` : unit;
    if (candidate.length > maxChars && current) {
      chunks.push(current);
      const overlap = tailForOverlap(current, overlapChars);
      current = overlap ? `${overlap}\n\n${unit}` : unit;
      // Degenerate case: overlap + unit still exceeds the budget — drop the
      // overlap rather than emitting an oversized chunk.
      if (current.length > maxChars) current = unit;
    } else {
      current = candidate;
    }
  }
  if (current.trim()) chunks.push(current.trim());

  return chunks;
}

// Joins per-page texts into one document string with paragraph breaks between
// pages, so chunking can respect page boundaries as paragraph boundaries.
export function pagesToText(pageTexts) {
  return (pageTexts || [])
    .map(p => String(p || '').trim())
    .filter(Boolean)
    .join('\n\n');
}
